export type ScheduleTab = "today" | "incomplete" | "finished" | "upcoming";

export const normalizeScheduleTab = (value: string | null): ScheduleTab => {
  if (value === "incomplete" || value === "finished" || value === "upcoming") {
    return value;
  }
  return "today";
};

export type ScheduleSortKey = "status" | "name" | "date" | "curriculum";

type Placed = { sequence_position?: number | null };

/**
 * Curriculum order: by `sequence_position`, ties kept in the server's order.
 *
 * Positions can tie, and the server sends a tie in the order the sequence walks
 * it, so a tie is never broken by name: that would show two lessons in an order
 * the student will not meet them in. Descending reverses the walk, ties too.
 *
 * A lesson with no position has no place in the curriculum to show, so it goes
 * last in both directions -- reversing the list should not lift the lessons we
 * cannot place above the ones we can.
 */
const compareCurriculum = (
  left: { lesson: Placed; index: number },
  right: { lesson: Placed; index: number },
  ascending: boolean,
) => {
  const leftPosition = left.lesson.sequence_position;
  const rightPosition = right.lesson.sequence_position;
  if (leftPosition == null && rightPosition != null) return 1;
  if (rightPosition == null && leftPosition != null) return -1;
  const result =
    leftPosition != null &&
    rightPosition != null &&
    leftPosition !== rightPosition
      ? leftPosition - rightPosition
      : left.index - right.index;
  return ascending ? result : -result;
};

export const sortScheduleLessons = <
  T extends {
    name: string;
    status: string;
    assigned_at?: string | null;
    created_at: string;
    sequence_position?: number | null;
  },
>(
  lessons: T[],
  sortBy: ScheduleSortKey,
  ascending: boolean,
): T[] => {
  if (sortBy === "curriculum") {
    return lessons
      .map((lesson, index) => ({ lesson, index }))
      .sort((left, right) => compareCurriculum(left, right, ascending))
      .map(({ lesson }) => lesson);
  }
  return [...lessons].sort((left, right) => {
    const leftValue =
      sortBy === "name"
        ? left.name.toLowerCase()
        : sortBy === "date"
          ? left.assigned_at || left.created_at
          : `${left.status}-${left.name.toLowerCase()}`;
    const rightValue =
      sortBy === "name"
        ? right.name.toLowerCase()
        : sortBy === "date"
          ? right.assigned_at || right.created_at
          : `${right.status}-${right.name.toLowerCase()}`;
    const result = leftValue.localeCompare(rightValue);
    return ascending ? result : -result;
  });
};

export const paginateScheduleLessons = <T>(
  lessons: T[],
  page: number,
  pageSize: number,
) => {
  const safePageSize = Math.max(1, pageSize);
  const pageCount = Math.max(1, Math.ceil(lessons.length / safePageSize));
  return {
    pageCount,
    lessons: lessons.slice((page - 1) * safePageSize, page * safePageSize),
  };
};
